import { useState, useEffect } from 'react';
import type { Product } from '../types';
import { loadProductsFromCSV } from '../utils/csvParser';

interface UseProductsReturn {
  products: Product[];
  regularProducts: Product[];
  offerProducts: Product[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to load products from CSV
 * Provides loading state, error handling, and automatic filtering
 */
export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const loadedProducts = await loadProductsFromCSV();
      setProducts(loadedProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
      console.error('Error in useProducts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Memoized filtered products
  const regularProducts = products; // Show all products in main grid, including offers
  const offerProducts = products.filter(p => p.offer);

  return {
    products,
    regularProducts,
    offerProducts,
    isLoading,
    error,
    refetch: fetchProducts,
  };
}

// Export individual product getter functions for convenience
export function getProductById(products: Product[], id: string): Product | undefined {
  return products.find(p => p.id === id);
}
